import type { ReactNode } from "react";
import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Heading, Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";

/**
 * A report tile, or a section heading over content that owns its frame.
 */
export const ReportCard = ({
  title,
  subtitle,
  note,
  actions,
  className,
  bodyClassName,
  bare = false,
  children,
}: {
  title: string;
  /** A line under the title, for example the span a chart covers. */
  subtitle?: ReactNode;
  /** Short qualifier, for example the aggregate behind the figure. */
  note?: string;
  /** Controls for this card alone, shown at the end of its title row. */
  actions?: ReactNode;
  className?: string;
  bodyClassName?: string;
  /**
   * A titled section instead of a card, for a block whose content brings
   * its own frames (tables and the review's rules).
   */
  bare?: boolean;
  children: ReactNode;
}) =>
  bare ? (
    <section
      aria-label={title}
      className={cn("flex min-w-0 flex-col gap-3 pt-2", className)}
    >
      <Heading level={2} variant="title" className="text-lg text-balance">
        {title}
      </Heading>
      {children}
    </section>
  ) : (
    <Card
      role="region"
      aria-label={title}
      className={cn(
        "flex min-w-0 flex-col gap-0 overflow-hidden py-0",
        className,
      )}
    >
      <Stack
        direction="row"
        justify="between"
        align="center"
        gap={2}
        className="w-full flex-none flex-wrap gap-y-1 px-4 pt-3 pb-2"
      >
        {/* The title takes the row to itself rather than being cut in half by its own
          controls. On a wide card everything still sits on one line; on a narrow one
          the controls drop beneath, which costs a few pixels of height and buys back
          the name of the thing being shown. */}
        <Stack gap={0} align="stretch" className="min-w-0 flex-1 basis-56">
          {/* A long name wraps rather than ending in an ellipsis. A card headed
            "Pr..." tells the reader nothing, and the second line costs less
            than the name does. */}
          <Heading level={2} variant="label" className="text-balance">
            {title}
          </Heading>
          {subtitle && (
            <Text variant="caption" tone="muted">
              {subtitle}
            </Text>
          )}
        </Stack>
        <Stack direction="row" align="center" gap={1} className="flex-none">
          {note && (
            <Text variant="caption" tone="muted">
              {note}
            </Text>
          )}
          {actions}
        </Stack>
      </Stack>
      <Stack
        gap={0}
        align="stretch"
        className={cn("min-h-0 flex-1", bodyClassName)}
      >
        {children}
      </Stack>
    </Card>
  );

/** A figure without its card, for the headline strip and the supporting row. */
export const InlineFigure = ({
  title,
  className,
  children,
}: {
  title: string;
  className?: string;
  children: ReactNode;
}) => (
  <Stack
    gap={0}
    align="stretch"
    className={cn("min-w-0 flex-1 px-4 py-3", className)}
  >
    <Text variant="caption" tone="muted" className="text-balance" title={title}>
      {title}
    </Text>
    {children}
  </Stack>
);
